import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../../store";

export enum QuoteRequestType {
  DESIGN_ONLY = "Design Only",
  QUOTE_ONLY = "Quote Only",
  DESIGN_AND_QUOTE = "Design & Quote",
}

export const STEP_PRECEDENCE: Record<string, number> = {
  "customer_details": 1,
  "customer-details": 1,
  "request_details": 2,
  "request-details": 2,
  "storage_types": 3,
  "storage-types": 3,
  "products": 4,
  "accessories": 5,
  "review": 6,
  "completed": 6,
  "approved": 6,
  "quote-created": 6,
};

export const computeUpdatedLastCompletedStep = (
  existingStep?: string,
  newStep?: string,
): string => {
  if (!existingStep) return newStep || "customer_details";
  if (!newStep) return existingStep;

  const existingRank =
    STEP_PRECEDENCE[existingStep.toLowerCase().replace(/_/g, "-")] || 0;
  const newRank =
    STEP_PRECEDENCE[newStep.toLowerCase().replace(/_/g, "-")] || 0;

  return existingRank >= newRank ? existingStep : newStep;
};

export interface SelectedStorageType {
  _id: string;
  category_id: string;
  name: string;
  image?: string;
  slug?: string;
  product_type_name?: string;
  label?: string;
  is_lean_category?: boolean;
}

export interface QuoteRequestCartItem {
  _id: string;
  variantId?: string;
  itemType: "product" | "accessory" | "replacement_parts";
  item_id: string;
  category_id: string;
  productId: string;
  title: string;
  qty: number;
  selections?: Record<string, any>;
  productData?: any;
}

export interface QuoteRequestDetails {
  _id: string;
  display_quote_request_id?: string;
  rev_no?: string;
  project_no?: string;
  customer_id?: any;
  company_name?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  request_type?: QuoteRequestType;
  description?: string;
  assigned_team_members?: any[];
  attachments?: any[];
  categories_snapshot?: SelectedStorageType[];
  products?: any[];
  current_step?: string;
  last_completed_step?: string;
  status?: string;
  currency?: string;
  conversion_rate?: number;
  customer_snapshot?: any;
  selected_change?: any;
  change_id?: string;
  sage_id?: string;
  sage_reference?: string;
  discount?: number;
  margin?: number;
  show_retail_price?: boolean;
}

interface QuoteRequestState {
  quoteRequestId: string | null;
  quoteRequestDetails: QuoteRequestDetails | null;
  selectedStorageTypes: SelectedStorageType[];
  cartItems: QuoteRequestCartItem[];
  additionalInfo: Record<string, { notes?: string }>;
  loading: boolean;
}

const initialState: QuoteRequestState = {
  quoteRequestId: null,
  quoteRequestDetails: null,
  selectedStorageTypes: [],
  cartItems: [],
  additionalInfo: {},
  loading: false,
};

export const CreateQuoteRequestStepsEnum = {
  CUSTOMER_DETAILS: "customer-details",
  REQUEST_DETAILS: "request-details",
  STORAGE_TYPES: "storage-types",
  PRODUCTS: "products",
  ACCESSORIES: "accessories",
  FLOOR_DETAILS: "floor-details",
  REVIEW: "review",
};

export const STATIC_GROUP_CHILDREN = [
  { key: CreateQuoteRequestStepsEnum.PRODUCTS, label: "Products" },
  {
    key: CreateQuoteRequestStepsEnum.ACCESSORIES,
    label: "Accessories and Replacement Parts",
  },
];

export const isMezzanineCategory = (cat: any): boolean => {
  const slug = (cat?.slug || cat?.key || "").toLowerCase();
  const name = (cat?.name || cat?.label || "").toLowerCase();
  const productTypeName = (cat?.product_type_name || "").toLowerCase();
  return (
    slug.includes("mezzanine") ||
    name.includes("mezzanine") ||
    productTypeName.includes("mezzanine")
  );
};

export const getChildrenForCategory = (group: SelectedStorageType | any): { key: string; label: string }[] => {
  if (isMezzanineCategory(group)) {
    return [{ key: CreateQuoteRequestStepsEnum.FLOOR_DETAILS, label: "Floor Details" }];
  }
  return STATIC_GROUP_CHILDREN;
};

const quoteRequestSlice = createSlice({
  name: "quoteRequest",
  initialState,
  reducers: {
    setQuoteRequestId: (state, action: PayloadAction<string | null>) => {
      state.quoteRequestId = action.payload;
    },
    setQuoteRequestDetails: (
      state,
      action: PayloadAction<QuoteRequestDetails | null>,
    ) => {
      state.quoteRequestDetails = action.payload;
      if (action.payload?.categories_snapshot) {
        state.selectedStorageTypes = action.payload.categories_snapshot.map(
          (cat: any) => ({
            _id: cat._id || cat.category_id,
            category_id: cat.category_id || cat._id,
            name: cat.name,
            image: cat.image_url || cat.image,
            slug: cat.slug || cat.key,
            product_type_name: cat.product_type_name || cat.name,
          }),
        );
      }
    },
    setSelectedStorageTypes: (
      state,
      action: PayloadAction<SelectedStorageType[]>,
    ) => {
      state.selectedStorageTypes = action.payload;
    },
    setCartItems: (state, action: PayloadAction<QuoteRequestCartItem[]>) => {
      state.cartItems = action.payload;
    },
    addCartItem: (state, action: PayloadAction<QuoteRequestCartItem>) => {
      const existingIdx = state.cartItems.findIndex(
        (item) =>
          item.item_id === action.payload.item_id &&
          item.productId === action.payload.productId &&
          item.variantId === action.payload.variantId &&
          item.itemType === action.payload.itemType,
      );
      if (existingIdx >= 0) {
        state.cartItems[existingIdx].qty += action.payload.qty;
      } else {
        state.cartItems.push(action.payload);
      }
    },
    updateCartItemQty: (
      state,
      action: PayloadAction<{ id: string; qty: number }>,
    ) => {
      const item = state.cartItems.find((i) => i._id === action.payload.id);
      if (item) {
        item.qty = Math.max(1, action.payload.qty);
      }
    },
    removeCartItem: (state, action: PayloadAction<string>) => {
      state.cartItems = state.cartItems.filter((i) => i._id !== action.payload);
    },
    setCategoryAdditionalInfo: (
      state,
      action: PayloadAction<{ categoryId: string; notes: string }>,
    ) => {
      const { categoryId, notes } = action.payload;
      state.additionalInfo[categoryId] = {
        ...state.additionalInfo[categoryId],
        notes,
      };
    },
    resetQuoteRequestState: (state) => {
      state.quoteRequestId = null;
      state.quoteRequestDetails = null;
      state.selectedStorageTypes = [];
      state.cartItems = [];
      state.additionalInfo = {};
      state.loading = false;
    },
  },
});

export const {
  setQuoteRequestId,
  setQuoteRequestDetails,
  setSelectedStorageTypes,
  setCartItems,
  addCartItem,
  updateCartItemQty,
  removeCartItem,
  setCategoryAdditionalInfo,
  resetQuoteRequestState,
} = quoteRequestSlice.actions;

// Selectors
export const selectQuoteRequestId = (state: RootState) =>
  state.quoteRequest.quoteRequestId;
export const selectQuoteRequestDetails = (state: RootState) =>
  state.quoteRequest.quoteRequestDetails;
export const selectSelectedStorageTypes = (state: RootState) =>
  state.quoteRequest.selectedStorageTypes;
export const selectCartItems = (state: RootState) =>
  state.quoteRequest.cartItems;
export const selectAdditionalInfo = (state: RootState) =>
  state.quoteRequest.additionalInfo;
export const selectCustomerSnapshot = (state: RootState) =>
  state.quoteRequest?.quoteRequestDetails?.customer_snapshot ||
  state.quoteRequest?.quoteRequestDetails?.selected_change?.customer_snapshot;
export const selectShowRetailPrice = (state: RootState) =>
  state.quoteRequest?.quoteRequestDetails?.customer_snapshot?.show_retail_price ??
  state.quoteRequest?.quoteRequestDetails?.show_retail_price ??
  state.quotation?.quotationDetails?.customer_snapshot?.show_retail_price ??
  state.quotation?.quotationDetails?.show_retail_price ??
  true;

export const selectStepPlaylist = createSelector(
  [selectSelectedStorageTypes],
  (selectedTypes: SelectedStorageType[]) => {
    const playlist: string[] = [
      CreateQuoteRequestStepsEnum.CUSTOMER_DETAILS,
      CreateQuoteRequestStepsEnum.STORAGE_TYPES,
    ];

    selectedTypes.forEach((type: SelectedStorageType) => {
      const categoryKey = type._id || type.category_id;
      const children = getChildrenForCategory(type);
      children.forEach((child: { key: string; label: string }) => {
        playlist.push(`${categoryKey}__${child.key}`);
      });
    });

    playlist.push(CreateQuoteRequestStepsEnum.REVIEW);

    return playlist;
  },
);

export default quoteRequestSlice.reducer;
